/**
 * CompanyFormFields — shared company field set (#1002)
 *
 * Single source for the company form fields and their validation, used by
 * CompanyFormDialog and the inline company creation in the work-order create
 * flow. `showExtended=false` renders only the essentials (name, business id,
 * phone, email) for compact inline use; address and billing details can be
 * edited later on the company page.
 */
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import {
  PRH_SEARCH_MIN_LENGTH,
  usePrhCompanySearch,
  type PrhCompany,
} from '@/hooks/usePrhCompanySearch';
import { LaborRateSelect } from '@/components/common/LaborRateSelect';

export interface CompanyFieldsData {
  name: string;
  businessId: string;
  phone: string;
  email: string;
  website: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  country: string;
  billingStreetAddress: string;
  billingPostalCode: string;
  billingCity: string;
  invoiceEmail: string;
  eInvoiceAddress: string;
  ovtId: string;
  /** Omitted on wizard drafts and callers predating #3066; then left untouched on save. */
  eInvoiceOperator?: string;
  paymentTermsDays: string;
  notes: string;
  /** Omitted on legacy wizard drafts that never collected a named rate. */
  laborRateId?: string;
}

export const initialCompanyFieldsData: CompanyFieldsData = {
  name: '',
  businessId: '',
  phone: '',
  email: '',
  website: '',
  streetAddress: '',
  postalCode: '',
  city: '',
  country: 'FI',
  billingStreetAddress: '',
  billingPostalCode: '',
  billingCity: '',
  invoiceEmail: '',
  eInvoiceAddress: '',
  ovtId: '',
  eInvoiceOperator: '',
  paymentTermsDays: '',
  notes: '',
  laborRateId: '',
};

export function validateCompanyFields(
  data: CompanyFieldsData,
  t: TFunction,
): Partial<Record<keyof CompanyFieldsData, string>> {
  const errors: Partial<Record<keyof CompanyFieldsData, string>> = {};
  if (!data.name.trim()) errors.name = t('validation.nameRequired');
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = t('validation.emailInvalid');
  }
  if (data.invoiceEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.invoiceEmail.trim())) {
    errors.invoiceEmail = t('validation.emailInvalid');
  }
  return errors;
}

/** Build the create/update payload from the field state (trims, empty → null). */
export function companyFieldsToPayload(data: CompanyFieldsData) {
  const parsedTerms = data.paymentTermsDays.trim() ? Number(data.paymentTermsDays.trim()) : null;
  return {
    name: data.name.trim(),
    businessId: data.businessId.trim() || null,
    phone: data.phone.trim() || null,
    email: data.email.trim() || null,
    website: data.website.trim() || null,
    streetAddress: data.streetAddress.trim() || null,
    postalCode: data.postalCode.trim() || null,
    city: data.city.trim() || null,
    country: data.country.trim() || 'FI',
    billingStreetAddress: data.billingStreetAddress.trim() || null,
    billingPostalCode: data.billingPostalCode.trim() || null,
    billingCity: data.billingCity.trim() || null,
    invoiceEmail: data.invoiceEmail.trim() || null,
    eInvoiceAddress: data.eInvoiceAddress.trim() || null,
    ovtId: data.ovtId.trim() || null,
    paymentTermsDays: parsedTerms != null && Number.isFinite(parsedTerms) ? parsedTerms : null,
    notes: data.notes.trim() || null,
    ...(data.laborRateId === undefined
      ? {}
      : { laborRateId: data.laborRateId.trim() || null }),
    ...(data.eInvoiceOperator === undefined
      ? {}
      : { eInvoiceOperator: data.eInvoiceOperator.trim() || null }),
  };
}

interface CompanyFormFieldsProps {
  data: CompanyFieldsData;
  errors: Partial<Record<keyof CompanyFieldsData, string>>;
  onChange: (field: keyof CompanyFieldsData, value: string) => void;
  /** Render the full field set (dialog) or only the essentials (inline create). */
  showExtended?: boolean;
  autoFocus?: boolean;
}

function applyPrhCompany(
  company: PrhCompany,
  onChange: (field: keyof CompanyFieldsData, value: string) => void,
) {
  onChange('name', company.name);
  onChange('businessId', company.businessId);
  const streetAddress = company.streetAddress?.trim();
  if (streetAddress) onChange('streetAddress', streetAddress);
  const postalCode = company.postalCode?.trim();
  if (postalCode) onChange('postalCode', postalCode);
  const city = company.city?.trim();
  if (city) onChange('city', city);
}

function prhOptionMeta(company: PrhCompany, t: TFunction): string {
  const city = company.city?.trim();
  return city
    ? t('prh.optionMetaWithCity', { businessId: company.businessId, city })
    : company.businessId;
}

export function CompanyFormFields({
  data,
  errors,
  onChange,
  showExtended = true,
  autoFocus,
}: CompanyFormFieldsProps) {
  const { t } = useTranslation('companies');
  const {
    data: prhCompanies = [],
    isFetching,
    isError,
    isFetched,
  } = usePrhCompanySearch(data.name);
  const canSearch = data.name.trim().length >= PRH_SEARCH_MIN_LENGTH;
  const isPrhLoading = canSearch && (isFetching || (!isFetched && !isError));
  const nameHelperText = errors.name
    ? errors.name
    : isError
      ? t('prh.searchFailed')
      : isPrhLoading
        ? t('prh.loading')
        : canSearch && isFetched && prhCompanies.length === 0
          ? t('prh.noResults')
          : t('prh.searchHint');

  return (
    <>
      {showExtended && (
        <Typography variant="subtitle2" color="text.secondary">{t('sections.basicInfo')}</Typography>
      )}
      <Autocomplete<PrhCompany, false, false, true>
        freeSolo
        autoHighlight
        clearOnBlur={false}
        forcePopupIcon={false}
        value={null}
        inputValue={data.name}
        options={prhCompanies}
        loading={isPrhLoading}
        loadingText={t('prh.loading')}
        filterOptions={(options) => options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        isOptionEqualToValue={(option, value) => option.businessId === value.businessId}
        onInputChange={(_event, value, reason) => {
          if (reason === 'reset') return;
          onChange('name', value);
        }}
        onChange={(_event, value) => {
          if (typeof value === 'string') {
            onChange('name', value);
            return;
          }
          if (value) applyPrhCompany(value, onChange);
        }}
        noOptionsText={
          isError
            ? t('prh.searchFailed')
            : canSearch
              ? t('prh.noResults')
              : t('prh.searchHint')
        }
        renderOption={(props, option) => (
          <li {...props} key={option.businessId}>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {prhOptionMeta(option, t)}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('fields.name')}
            error={!!errors.name}
            helperText={nameHelperText}
            fullWidth
            required
            autoFocus={autoFocus}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('fields.businessId')}
          value={data.businessId}
          onChange={(e) => onChange('businessId', e.target.value)}
          error={!!errors.businessId}
          // Hint unless there is an error to show — otherwise the hint masks it (#2726).
                  helperText={errors.businessId ?? t('fields.businessIdHint')}
          fullWidth
        />
        <TextField label={t('fields.phone')} value={data.phone} onChange={(e) => onChange('phone', e.target.value)} fullWidth />
      </Stack>
      {showExtended ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label={t('fields.email')} value={data.email} onChange={(e) => onChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} type="email" fullWidth />
          <TextField label={t('fields.website')} value={data.website} onChange={(e) => onChange('website', e.target.value)} fullWidth />
        </Stack>
      ) : (
        <TextField label={t('fields.email')} value={data.email} onChange={(e) => onChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} type="email" fullWidth />
      )}

      {showExtended && (
        <>
          <Divider />
          <Typography variant="subtitle2" color="text.secondary">{t('sections.address')}</Typography>
          <TextField label={t('fields.streetAddress')} value={data.streetAddress} onChange={(e) => onChange('streetAddress', e.target.value)} fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('fields.postalCode')} value={data.postalCode} onChange={(e) => onChange('postalCode', e.target.value)} sx={{ maxWidth: { sm: 160 } }} fullWidth />
            <TextField label={t('fields.city')} value={data.city} onChange={(e) => onChange('city', e.target.value)} fullWidth />
            <TextField label={t('fields.country')} value={data.country} onChange={(e) => onChange('country', e.target.value)} sx={{ maxWidth: { sm: 100 } }} fullWidth />
          </Stack>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">{t('billing.title')}</Typography>
          <TextField label={t('billing.billingStreetAddress')} value={data.billingStreetAddress} onChange={(e) => onChange('billingStreetAddress', e.target.value)} fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('billing.billingPostalCode')} value={data.billingPostalCode} onChange={(e) => onChange('billingPostalCode', e.target.value)} sx={{ maxWidth: { sm: 160 } }} fullWidth />
            <TextField label={t('billing.billingCity')} value={data.billingCity} onChange={(e) => onChange('billingCity', e.target.value)} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('billing.invoiceEmail')} value={data.invoiceEmail} onChange={(e) => onChange('invoiceEmail', e.target.value)} error={!!errors.invoiceEmail} helperText={errors.invoiceEmail ?? t('billing.invoiceEmailHelp')} type="email" fullWidth />
            <TextField label={t('billing.eInvoiceAddress')} value={data.eInvoiceAddress} onChange={(e) => onChange('eInvoiceAddress', e.target.value)} helperText={t('billing.eInvoiceAddressHint')} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('billing.ovtId')} value={data.ovtId} onChange={(e) => onChange('ovtId', e.target.value)} helperText={t('billing.ovtIdHint')} fullWidth />
            <TextField label={t('billing.eInvoiceOperator')} value={data.eInvoiceOperator ?? ''} onChange={(e) => onChange('eInvoiceOperator', e.target.value)} helperText={t('billing.eInvoiceOperatorHint')} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('billing.paymentTermsDays')} value={data.paymentTermsDays} onChange={(e) => onChange('paymentTermsDays', e.target.value)} type="number" sx={{ maxWidth: { sm: 180 } }} fullWidth />
          </Stack>
          <LaborRateSelect
            value={data.laborRateId || null}
            onChange={(id) => onChange('laborRateId', id ?? '')}
            label={t('billing.laborRate')}
            helperText={t('billing.laborRateHint')}
            emptyLabel={t('billing.laborRateWorkshopDefault')}
          />

          <Divider />
          <TextField label={t('fields.notes')} value={data.notes} onChange={(e) => onChange('notes', e.target.value)} fullWidth multiline rows={3} />
        </>
      )}
    </>
  );
}
